import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import { CoordStripCrossfadeHost } from './CoordStripCrossfadeHost';

/**
 * GlobalNavCoordStrip — the shared 26px coordinate strip that anchors every
 * global-nav layer (WEB-333, DS 1.2.0 `o-globalnav__coord`). It is the
 * continuity device of the nav system: a mono, uppercase, letter-spaced band
 * that tells the visitor exactly which host + locus they are standing in.
 *
 * Reference: `docs/design-system/1.2.0/nav/loci-globalnav.css` (`.o-globalnav__coord`).
 *
 * Three segments, space-between (from 721px):
 *  - LEFT  host segment — 5px pulse dot (`box-shadow: 0 0 8px currentColor`,
 *    breathing opacity, motion-reduce guarded via the global
 *    `animate-loci-breathe` utility) + the layer eyebrow in `--gn-accent-glow`.
 *  - CENTER position segment — mono coords, tabular-nums.
 *  - RIGHT meta segment — `<b>` children pick up `--fg-2` emphasis.
 *
 * At ≤720px (WEB-621 / WEB-1136 / DES-61): when both `position` and `meta`
 * are present, they share one slot and swap every 6s of a 12s cycle (opacity
 * only — no reflow), driven by ONE `data-face` attribute on
 * {@link CoordStripCrossfadeHost} so the two can never show together
 * (WEB-1131). From 721px both faces stay visible. Under
 * `prefers-reduced-motion: reduce` the slot shows coordinates statically.
 * When only one of the two is present, that value renders with no animation.
 *
 * Accent-agnostic: callers set `--gn-accent-glow` (and friends) as inline
 * custom properties on their header root via {@link GLOBAL_NAV_ACCENT_VARS}.
 */

export type GlobalNavLayer = 'apex' | 'city' | 'app';

/**
 * Per-layer accent custom properties (DS 1.2.0 `[data-layer]` /
 * `[data-mode="studio"]` overrides). Spread onto each header root's `style`
 * so every accent-bearing descendant can resolve `var(--gn-accent*)`.
 */
// City uses the theme-aware skyline *fg* role, not the invariant glow hex.
// `--clt-skyline-fg` is `#4fc4c4` in dark (byte-identical to `--clt-skyline-glow`)
// and `#0a6363` in light so coord/header ink clears AA on paper (FRE-1356).
// Inline `--gn-accent*` would otherwise beat the CSS light delta.
const SKYLINE_ACCENT_VARS: React.CSSProperties = {
  '--gn-accent': 'var(--clt-skyline)',
  '--gn-accent-glow': 'var(--clt-skyline-fg)',
  '--gn-accent-wash': 'var(--clt-skyline-wash)',
} as React.CSSProperties;

const APP_ACCENT_VARS: React.CSSProperties = {
  '--gn-accent': 'var(--nav-accent)',
  '--gn-accent-glow': 'var(--nav-accent)',
  '--gn-accent-wash': 'color-mix(in srgb, var(--nav-accent) 12%, transparent)',
} as React.CSSProperties;

export const GLOBAL_NAV_ACCENT_VARS: Readonly<
  Record<GlobalNavLayer | 'studio', React.CSSProperties>
> = {
  apex: {
    '--gn-accent': 'var(--loci-amber)',
    '--gn-accent-glow': 'var(--loci-amber-light)',
    '--gn-accent-wash': 'rgba(255, 184, 0, 0.10)',
  } as React.CSSProperties,
  city: SKYLINE_ACCENT_VARS,
  app: APP_ACCENT_VARS,
  studio: {
    '--gn-accent': 'var(--clt-ember)',
    '--gn-accent-glow': 'var(--clt-ember-light)',
    '--gn-accent-wash': 'rgba(232, 131, 58, 0.12)',
  } as React.CSSProperties,
};

/**
 * Focus-visible ring shared by every global-nav affordance (links, CTA pills,
 * the menu trigger, the sheet close). Lives here rather than in a per-surface
 * module so surface-neutral primitives — `NavMenuSheet` and friends — can
 * reach it without importing city-scoped code. `cityHeaderShared` re-exports
 * it as `FOCUS_RING` for the existing city call sites.
 */
export const GN_FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--loci-color-ring,#0e7b7b)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--loci-color-background,#0F141F)]';

/**
 * Core glow-underline Tailwind utilities shared by all active nav link
 * variants (DS 1.2.0 `o-globalnav__link[aria-current="page"]::after`).
 *
 * Composed into {@link GN_ACTIVE_LINK_GLOW_STATIC} and
 * {@link GN_ACTIVE_LINK_GLOW_TRANSITION} — do not use directly in JSX.
 */
export const GN_ACTIVE_LINK_GLOW_CORE =
  'after:h-[1.5px] after:rounded-full after:bg-[var(--gn-accent-glow)] after:shadow-[0_0_6px_var(--gn-accent-glow)]';

/**
 * Active-link glow underline for **static** nav links (CityHeader, SiteHeaderApexView,
 * SiteHeaderMarketingView). Positioned with explicit `after:left-3 after:right-3`.
 * Pair with the link's color token (e.g. `text-[var(--fg-1)]` or `text-white`).
 */
export const GN_ACTIVE_LINK_GLOW_STATIC = `after:absolute after:bottom-1 after:left-3 after:right-3 ${GN_ACTIVE_LINK_GLOW_CORE} after:content-[""]`;

/**
 * Active-link glow underline for **transition** nav links (AppHeader nav +
 * categories slot). Uses `after:inset-x-3` and opacity-transition so the
 * underline fades in/out smoothly as `after:opacity-*` is toggled.
 */
export const GN_ACTIVE_LINK_GLOW_TRANSITION = `after:pointer-events-none after:absolute after:inset-x-3 after:bottom-1 ${GN_ACTIVE_LINK_GLOW_CORE} after:opacity-0 after:transition-opacity after:duration-[180ms]`;

export interface GlobalNavCoordStripProps {
  /** Layer eyebrow rendered in the accent-glow host segment, e.g. `CITY · CLT.LOCI.CITY`. */
  eyebrow: string;
  /**
   * Center position segment (mono coords). At ≤720px, when `meta` is also
   * provided, shares the right slot in the 12s opacity crossfade (WEB-621).
   */
  position?: React.ReactNode;
  /** Right meta segment. `<b>` children render `--fg-2` medium per the reference. */
  meta?: React.ReactNode;
  className?: string;
  /** Inline style — typically a {@link GLOBAL_NAV_ACCENT_VARS} entry when the strip mounts standalone. */
  style?: React.CSSProperties;
  testId?: string;
  dotTestId?: string;
  /**
   * Test id for the eyebrow span. Lets callers assert the host label
   * byte-exactly (`.textContent === …`) instead of matching the whole strip —
   * the SSR/CSR parity guard added with WEB-824.
   */
  eyebrowTestId?: string;
  positionTestId?: string;
  metaTestId?: string;
  /**
   * FRE-1362: group the position segment with meta on the right (from 721px)
   * instead of centering it between host and meta. Marketing-city only today.
   */
  groupCoordsRight?: boolean;
  /**
   * WEB-1131: one string for assistive tech. When set, the strip announces
   * this (sr-only) and hides its visual segments from the accessibility tree,
   * so the stacked crossfade layers never read as one run-together string.
   */
  accessibleLabel?: string;
}

const POSITION_LAYER_CLASS =
  "coord-strip-layer coord-strip-layer--a relative min-w-0 truncate whitespace-nowrap [font-feature-settings:'tnum'_1]";

// FRE-1362 / WEB-1136: from 721px (`styles/overrides/chrome.css`) this margin
// groups position + meta on the right (design `.o-globalnav__coord-cycle`)
// while the host eyebrow stays pinned left. An auto margin absorbs the free
// space, so the two cells keep only the strip's `gap-4` (16px) between them.
// At ≤720 the crossfade host places the readout. Opt-in via `groupCoordsRight`
// so only the marketing-city strip takes it; apex/app keep a centered position.
const POSITION_GROUP_RIGHT_CLASS = 'coord-strip-group-right';

const META_LAYER_CLASS =
  'coord-strip-layer coord-strip-layer--b inline-flex shrink-0 items-center gap-3.5 [&_b]:font-medium [&_b]:text-fg-2';

export function GlobalNavCoordStrip({
  eyebrow,
  position,
  meta,
  className,
  style,
  testId = 'global-nav-coord-strip',
  dotTestId,
  eyebrowTestId,
  positionTestId,
  metaTestId,
  groupCoordsRight = false,
  accessibleLabel,
}: GlobalNavCoordStripProps): React.ReactElement {
  const hasPosition = position != null;
  const hideVisualFromAt = accessibleLabel !== undefined ? true : undefined;
  const hasMeta = meta != null;
  const hasCrossfade = hasPosition && hasMeta;
  const positionClassName = cn(
    POSITION_LAYER_CLASS,
    groupCoordsRight && POSITION_GROUP_RIGHT_CLASS,
  );

  return (
    <div
      data-testid={testId}
      style={style}
      className={cn(
        // `min-w-0`: a flex row's default `min-width: auto` is the nowrap
        // content size. At 320 the eyebrow + degree coords exceed the
        // viewport (WEB-722 measured 343px) and expand document scrollWidth.
        'page-chrome-inline flex h-[26px] w-full min-w-0 items-center justify-between gap-4 border-b border-ds-border bg-bg font-mono text-micro tracking-[0.1em] text-fg-3 md:tracking-[0.18em]',
        className,
      )}
    >
      {accessibleLabel !== undefined ? <span className="sr-only">{accessibleLabel}</span> : null}
      {/* `min-w-0` (not shrink-0): at ≤720 the crossfade host reserves 190px, so
          the host eyebrow must be allowed to shrink for `truncate` to engage.
          `gn-coord-host` is a pure CSS hook — FRE-1362 hides this whole
          segment ≤440px on the marketing-city surface only, scoped via
          `.city-coord-strip .gn-coord-host` in `styles/overrides/chrome.css` (`@layer
          overrides`, since it must beat this `inline-flex` utility). */}
      <span
        aria-hidden={hideVisualFromAt}
        className="gn-coord-host inline-flex min-w-0 items-center gap-2 font-medium text-[color:var(--gn-accent-glow)]"
      >
        <span
          aria-hidden="true"
          data-testid={dotTestId}
          className="animate-loci-breathe inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-current shadow-[0_0_8px_currentColor]"
        />
        <span data-testid={eyebrowTestId} className="truncate">
          {eyebrow}
        </span>
      </span>

      {hasCrossfade ? (
        // `.coord-strip-cycle` unwraps (`display: contents`) from 721px so
        // position + meta rejoin this row. At ≤720 it is the opacity stack.
        <CoordStripCrossfadeHost
          ariaHidden={hideVisualFromAt}
          testId={metaTestId != null ? `${metaTestId}-crossfade` : undefined}
          className="coord-strip-cycle"
        >
          <span data-testid={positionTestId} className={positionClassName}>
            {position}
          </span>
          <span data-testid={metaTestId} className={META_LAYER_CLASS}>
            {meta}
          </span>
        </CoordStripCrossfadeHost>
      ) : null}

      {hasPosition && !hasMeta ? (
        <span
          aria-hidden={hideVisualFromAt}
          data-testid={positionTestId}
          className={positionClassName}
        >
          {position}
        </span>
      ) : null}

      {!hasPosition && hasMeta ? (
        <span aria-hidden={hideVisualFromAt} data-testid={metaTestId} className={META_LAYER_CLASS}>
          {meta}
        </span>
      ) : null}
    </div>
  );
}
